import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import { Card, FormGroupContext, Pagination } from '@ksyos/design-system';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { FundingErrorCode, FundingType } from '@common/enums';
import type { nl } from '@common/translate/nlMain';
import type { Uuid } from '@common/utils/uuid';
import type { IOption } from '@frontend/components/interfaces';
import { ExportStatusFilter, ManagerAuthorization } from '@common/enums';
import { FundingErrorInformation } from '@common/enums/fundingErrorInformation';
import { NotFundedReason } from '@common/enums/notFundedReason';
import { isNonNullable } from '@common/utils/nonNullableTypeUtils';
import { normalizeSearchText } from '@common/utils/normalizeSearchText';
import { searchTextMatchesProcessId } from '@common/utils/searchTextMatchesProcessId';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { uuidv4 } from '@common/utils/uuid';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import type { Financing } from './financing';
import type { FinancingSearchInputFilters } from './financingSearchInputFilters';
import { getSelectedLanguage } from '../../../../../translate';
import { Loader } from '../../../../elements/common';
import { NoFinancingSearchResults } from '../../NoFinancingSearchResults';
import { OtherFundingErrorInformationModal } from '../OtherFundingErrorInformationModal';
import { isAtLeastOneFilterSelected } from '../searchHelperFunctions';
import { FinancingProcessesFooterButtons } from './FinancingProcessesFooterButtons';
import { FinancingReprocessConfirmationModal } from './FinancingReprocessConfirmationModal';
import { FinancingSearchFilters } from './financingSearchFilters/FinancingSearchFilters';
import { getIsUsedForReprocessing, getSelectedItemsInDateOrder } from './financingTable/financingHelperFunctions';
import { FinancingTableOld } from './financingTable/FinancingTableOld';
import { getFilters } from './getFilters';

type PushIntoItemsArgs = {
    allFinanceItemsSelected: boolean;
    declarationAndInvoiceItems: Financing[];
    exportStatus?: ExportStatusFilter;
    isSelectedFinancingItemsEmpty: boolean | undefined;
    selectedFinancingItemIds: string[];
};

type ProcessDeclarationAndInvoiceItemArgs = {
    allFinanceItemsSelected: boolean;
    declarationAndInvoiceItem: Financing;
    isItemChecked: boolean;
    isSelectedFinancingItemsEmpty: boolean | undefined;
};

export type FundingErrorInformationType = keyof typeof nl.fundingErrorInformationEnum;

let stopReprocessing = false;
export function FinancingOld() {
    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    assertHasAuthorization(ManagerAuthorization.financeFunding);

    const initialFinancingSearchFilterState: FinancingSearchInputFilters = {
        exportStatus: undefined,
        noInvoiceNorDeclaration: false,
        onlyDeclarations: false,
        onlyInvoices: false,
        treatedStatus: undefined,
    };

    const [numberOfActivitiesToReprocess, setNumberOfActivitiesToReprocess] = useState<number>(0);

    const [financingItems, setFinancingItems] = useState<Financing[]>([]);
    const [financingItemIndex, setFinancingItemIndex] = useState<number>(0);
    const [financingSearchFilters, setFinancingSearchFilters] = useState<FinancingSearchInputFilters>(
        initialFinancingSearchFilterState
    );
    const [fundingErrors, setFundingErrors] = useState<IOption<FundingErrorCode>[]>([]);
    const [fundingErrorInformations, setFundingErrorInformations] = useState<IOption<string>[]>([]);
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [notFundedReasonValues, setNotFundedReasonValues] = useState<IOption<NotFundedReason>[]>([]);
    const [reprocessedActivities, setReprocessedActivities] = useState<string[]>([]);
    const [allFinanceItemsSelected, setAllFinanceItemsSelected] = useState(false);
    const [selectedFundingTypeAction, setSelectedFundingTypeAction] = useState<FundingType | null>(null);
    const [selectedFinancingItemIds, setSelectedFinancingItemIds] = useState<string[]>([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showOtherFundingErrorInformationModal, setShowOtherFundingErrorInformationModal] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [uniqueIdsTotal, setUniqueIdsTotal] = useState(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [pageSize, setPageSize] = useState(20);
    const [searchTrigger, setSearchTrigger] = useState<Uuid | null>(null);

    const { exportStatus, searchField, searchText } = financingSearchFilters;

    useEffect(() => {
        const load = async () => {
            await Promise.all([loadFundingErrors(), loadFundingErrorInformations(), loadNotFundedReasons()]);
        };

        load();

        return () => {
            stopReprocessing = true;
        };
    }, []);

    useEffect(() => {
        handleSetAllFinanceItemsSelected();
    }, [selectedFinancingItemIds.length]);

    useEffect(() => {
        if (!isInitialLoad) {
            search();
        }
    }, [searchTrigger, page, pageSize]);

    useEffect(() => {
        clearSearchResults();
    }, [financingSearchFilters]);

    return (
        <>
            <OtherFundingErrorInformationModal
                onClose={() => setShowOtherFundingErrorInformationModal(false)}
                onUpdateFundingErrorInformation={(fundingErrorInfo) =>
                    handleUpdateFundingErrorInformation(financingItemIndex, fundingErrorInfo)
                }
                showOtherFundingErrorInformationModal={showOtherFundingErrorInformationModal}
            />
            <FinancingReprocessConfirmationModal
                numberOfActivitiesToReprocess={numberOfActivitiesToReprocess}
                onClose={async () => setShowConfirmation(false)}
                onReprocessDeclarations={reprocessDeclarations}
                selectedFundingTypeAction={selectedFundingTypeAction}
                showConfirmation={showConfirmation}
            />
            <>
                <FormGroupContext.Provider
                    value={{
                        size: 'large',
                        variant: 'outline',
                    }}
                >
                    <FinancingSearchFilters
                        disableSearch={
                            isSearching || isReprocessing || !isAtLeastOneFilterSelected(financingSearchFilters)
                        }
                        financingSearchFilters={financingSearchFilters}
                        fundingErrorInformations={fundingErrorInformations}
                        fundingErrors={fundingErrors}
                        notFundedReasonValues={notFundedReasonValues}
                        onSearch={handleSearch}
                        onSetFinancingSearchFilters={setFinancingSearchFilters}
                    />
                </FormGroupContext.Provider>
                {financingItems.length > 0 && (
                    <>
                        <Card>
                            <Card.Header title={getSelectedLanguage().common.searchResults} />
                            <FinancingTableOld
                                allFinanceItemsSelected={allFinanceItemsSelected}
                                items={financingItems}
                                onChangeFundingErrorInformation={(value, index) => {
                                    handleChangeFundingErrorInformation(value, index);
                                }}
                                onSelectAllFinanceItems={(value) => {
                                    setFinancingItems(
                                        financingItems.map((item) => ({
                                            ...item,
                                            checked: getIsUsedForReprocessing(financingItems, item) && value,
                                        }))
                                    );

                                    setAllFinanceItemsSelected(value);
                                    if (value) {
                                        setSelectedFinancingItemIds([]);
                                    }
                                }}
                                onSelectFinancingItem={handleSelectFinancingItem}
                                reprocessedActivities={reprocessedActivities}
                            />
                            <Card.Footer>
                                <Pagination
                                    isDisabled={isSearching}
                                    onChange={handleNavigation}
                                    page={page}
                                    pageSize={pageSize}
                                    totalItems={total}
                                />
                            </Card.Footer>
                        </Card>
                        <FinancingProcessesFooterButtons
                            disableProcessing={
                                (selectedFinancingItemIds.length === 0 && !allFinanceItemsSelected) || isReprocessing
                            }
                            disableReporting={
                                isSearching ||
                                !isAtLeastOneFilterSelected(financingSearchFilters) ||
                                !financingItems.length
                            }
                            isReprocessing={isReprocessing}
                            items={financingItems}
                            onDownloadReport={handleDownloadReport}
                            onProcessFunding={handleShowConfirmation}
                            onStopReprocessing={() => (stopReprocessing = true)}
                        />
                    </>
                )}
                {(isSearching || isReprocessing) && <Loader fixedCentered />}
                {!isSearching && (
                    <NoFinancingSearchResults
                        isInitialLoad={isInitialLoad}
                        numberOfItems={financingItems?.length || 0}
                    />
                )}
            </>
        </>
    );

    async function handleNavigation(params: PaginationEventHandlersParams) {
        setPage(params.page);
        setPageSize(params.pageSize);
    }

    async function loadFundingErrorInformations() {
        const result = await abortableFetch.fetchJson(
            '/api/finance/funding/fundingErrorInformationFilterOptions',
            'GET'
        );

        const fundingErrorInformationsArray = result.json.items.map((value: FundingErrorInformation) => {
            return getfundingErrorInformationObject(value);
        });
        const orderedFundingErrorInformations = sortBy(fundingErrorInformationsArray, [(item) => item.label]);

        setFundingErrorInformations(orderedFundingErrorInformations);
    }

    async function loadNotFundedReasons() {
        const result = await abortableFetch.fetchJson(
            '/api/requests/getFinancingFilterValues?type=notFundedReason',
            'GET'
        );
        const response = result.json.items as NotFundedReason[];
        setNotFundedReasonValues(
            response.map((reason) => {
                return {
                    label: language.notFundedReason[reason],
                    value: reason,
                };
            })
        );
    }

    async function loadFundingErrors() {
        const result = await abortableFetch.fetchJson(
            '/api/requests/getFinancingFilterValues?type=fundingError',
            'GET'
        );
        const response = result.json.items as FundingErrorCode[];
        setFundingErrors(
            response.map((fundingError) => {
                return { label: language.fundingErrorCode[fundingError], value: fundingError };
            })
        );
    }

    function clearSearchResults() {
        setAllFinanceItemsSelected(false);
        setSelectedFinancingItemIds([]);
        setFinancingItems([]);
        setIsInitialLoad(false);
        setPage(1);
    }

    async function handleSearch() {
        clearSearchResults();
        setSearchTrigger(uuidv4());
    }

    async function processData(items: Financing[], isSelectedFinancingItemsEmpty: boolean | undefined) {
        const declarationAndInvoiceItems = processDeclarationItems({
            allFinanceItemsSelected,
            declarationAndInvoiceItems: items,
            exportStatus,
            isSelectedFinancingItemsEmpty,
            selectedFinancingItemIds,
        });

        return declarationAndInvoiceItems;
    }

    async function search(isSelectedFinancingItemsEmpty?: boolean) {
        setIsSearching(true);

        if (!isAtLeastOneFilterSelected(financingSearchFilters)) {
            setIsSearching(false);

            return;
        }

        if (isWrongSearchCondition(searchText, searchField)) {
            setTotal(0);
            setFinancingItems([]);
            setIsSearching(false);

            return;
        }

        const data = {
            filter: {
                ...getFilters(financingSearchFilters),
                paging: {
                    limit: pageSize,
                    page,
                },
            },
        };

        const result = await abortableFetch.fetchJson(
            '/api/requests/getDeclarationsAndInvoicesForProcesses',
            'POST',
            data
        );

        const items: Financing[] = result.json.items;
        const declarationAndInvoiceItems = await processData(items, isSelectedFinancingItemsEmpty);

        setTotal(result.json.total);
        setIsSearching(false);
        setFinancingItems(declarationAndInvoiceItems);
        setReprocessedActivities([]);

        setUniqueIdsTotal(result.json.uniqueIdsTotal);
    }

    function handleShowConfirmation(selectedFundingType?: FundingType) {
        const activitiesToRepro = allFinanceItemsSelected ? uniqueIdsTotal : selectedFinancingItemIds.length;

        setNumberOfActivitiesToReprocess(activitiesToRepro);
        setShowConfirmation(true);
        setSelectedFundingTypeAction(selectedFundingType ? selectedFundingType : null);
    }

    type ProcessedRequest = {
        update: {
            fundingError: FundingErrorCode | null;
            id: string;
            notFundedReason: NotFundedReason | null;
        };
    };

    async function reprocessRequests(requests: Financing[], reprocessReason: string) {
        const newFinancingItems = [...financingItems];
        const newReprocessedActivities = [...requests.map((request) => request.id)];
        setReprocessedActivities(newReprocessedActivities);

        for (const request of requests) {
            if (stopReprocessing) {
                break;
            }
            const result = await abortableFetch.fetchJson(`/api/funding/reprocess/${request.id}`, 'POST', {
                reason: reprocessReason,
                setFundingType: selectedFundingTypeAction,
            });
            if (result.json?.update) {
                const { update } = result.json as ProcessedRequest;
                if (newFinancingItems) {
                    const foundItemIndex = newFinancingItems.findIndex((r) => r.id === update.id);
                    if (foundItemIndex !== -1) {
                        newFinancingItems[foundItemIndex] = {
                            ...newFinancingItems[foundItemIndex],
                            fundingError: update.fundingError,
                            notFundedReason: update.notFundedReason,
                        };
                    }
                    const indexUpdatedActivity = newReprocessedActivities.indexOf(update.id);
                    newReprocessedActivities.splice(indexUpdatedActivity, 1);
                }
            }
            setReprocessedActivities(newReprocessedActivities);
        }
        setFinancingItems(newFinancingItems);
    }

    async function reprocessDeclarations(reprocessReason = '') {
        stopReprocessing = false;
        setShowConfirmation(false);
        setIsReprocessing(true);

        const requests = await getRequestsToReprocess();

        if (requests.length > 0) {
            await reprocessRequests(requests, reprocessReason);
        }

        setSelectedFundingTypeAction(null);

        await Promise.all([search(true), loadFundingErrorInformations(), loadNotFundedReasons(), loadFundingErrors()]);
        setAllFinanceItemsSelected(false);
        setIsReprocessing(false);
        setSelectedFinancingItemIds([]);
    }

    async function getRequestsToReprocess() {
        let requests: Financing[] = [];
        if (allFinanceItemsSelected) {
            const data = {
                filter: getFilters(financingSearchFilters),
                onlyRequestIds: true,
            };

            const result = await abortableFetch.fetchJson(
                '/api/requests/getDeclarationsAndInvoicesForProcesses',
                'POST',
                data
            );

            requests = result.json.items || [];
        } else {
            if (financingItems.length > 0) {
                requests = getSelectedItemsInDateOrder(financingItems, selectedFinancingItemIds);
            }
        }

        return requests;
    }

    function handleChangeFundingErrorInformation(value: FundingErrorInformation | null, index: number) {
        if (value === 'other') {
            setShowOtherFundingErrorInformationModal(true);
            setFinancingItemIndex(index);
        } else {
            handleUpdateFundingErrorInformation(index, value);
        }
    }

    async function handleUpdateFundingErrorInformation(
        index: number,
        fundingErrorInfo: FundingErrorInformation | null
    ) {
        if (!financingItems.length) {
            return;
        }

        const item = financingItems[index];

        await abortableFetch.fetchJson(
            `/api/finance/fundingResults/${item.processId}/updateFundingErrorInformation`,
            'POST',
            {
                fundingErrorInformation: fundingErrorInfo,
            }
        );

        const newItems = [...financingItems];
        newItems[index] = { ...financingItems[index], fundingErrorInformation: fundingErrorInfo };

        if (fundingErrorInfo) {
            updateFundingErrorInformationOptions(fundingErrorInfo);
        }
        setFinancingItems(newItems);

        setFinancingSearchFilters({ ...financingSearchFilters, fundingErrorInformation: undefined });
    }

    function updateFundingErrorInformationOptions(fundingErrorInf: FundingErrorInformation) {
        const fundingErrorInfoObject = getfundingErrorInformationObject(fundingErrorInf);
        let newFundingErrorInformations: IOption<string>[] = [...fundingErrorInformations];

        const newValueInOptions = fundingErrorInformations.find((info) => info.value === fundingErrorInf);

        if (!newValueInOptions) {
            newFundingErrorInformations = [...newFundingErrorInformations, fundingErrorInfoObject];
        }

        setFundingErrorInformations(newFundingErrorInformations);
    }

    function handleSelectFinancingItem(id: string, value: boolean) {
        if (!financingItems.length) {
            return;
        }
        const item = financingItems.find((financeItem) => {
            return financeItem.id === id && getIsUsedForReprocessing(financingItems, financeItem);
        });

        if (item) {
            handleSetFinancingItemsChecked(item.id, value);

            if (!value) {
                if (allFinanceItemsSelected) {
                    const selectedItemIds = financingItems
                        .filter((financingItem) => financingItem !== item)
                        .map((i) => i.id);
                    setSelectedFinancingItemIds(selectedItemIds);
                    setAllFinanceItemsSelected(false);

                    return;
                }
                setSelectedFinancingItemIds(
                    selectedFinancingItemIds.filter((selectedFinancingItemId) => selectedFinancingItemId !== id)
                );

                return;
            }
            setSelectedFinancingItemIds([...selectedFinancingItemIds, id]);
        }
    }

    function handleSetFinancingItemsChecked(id: string, value: boolean) {
        setFinancingItems((prevItems) => {
            const newItems = prevItems ? [...prevItems] : [];
            const itemIndex = newItems.findIndex((newItem) => {
                return newItem.id === id && getIsUsedForReprocessing(newItems, newItem);
            });
            if (itemIndex !== -1) {
                newItems[itemIndex] = {
                    ...newItems[itemIndex],
                    checked: value,
                };
            }

            return newItems;
        });
    }

    function handleSetAllFinanceItemsSelected() {
        if (selectedFinancingItemIds.length > 0) {
            if (selectedFinancingItemIds.length === uniqueIdsTotal) {
                if (!allFinanceItemsSelected) {
                    setAllFinanceItemsSelected(false);
                }
                setAllFinanceItemsSelected(true);
            }
        }
    }

    function handleDownloadReport() {
        const urlBuilder = new UrlBuilder('/api/requests/exportDeclarationsAndInvoicesForProcesses');

        const { batchRun, ...remainingFilters } = getFilters(financingSearchFilters);

        for (const [key, value] of Object.entries(remainingFilters)) {
            if (value !== null && value !== undefined) {
                urlBuilder.add(key, value);
            }
        }

        if (batchRun?.batchType && batchRun?.runNumber !== null) {
            urlBuilder.add('batchType', batchRun.batchType);
            urlBuilder.add('runNumber', batchRun.runNumber);
        }

        window.open(urlBuilder.build());
    }

    function getfundingErrorInformationObject(value: FundingErrorInformation) {
        if (value in FundingErrorInformation) {
            return { label: language.fundingErrorInformationEnum[value], value };
        }

        return {
            label: value,
            value,
        };
    }
}

export function processDeclarationItems(args: PushIntoItemsArgs) {
    const {
        declarationAndInvoiceItems,
        isSelectedFinancingItemsEmpty,
        selectedFinancingItemIds,
        allFinanceItemsSelected,
        exportStatus,
    } = args;

    const processedDeclarationAndInvoiceItems = declarationAndInvoiceItems.map((item) => {
        const isItemChecked = selectedFinancingItemIds.some(
            (selectedFinancingItem) => selectedFinancingItem === item.id
        );
        const declarationAndInvoiceItem = processDeclarationAndInvoiceItem({
            allFinanceItemsSelected,
            declarationAndInvoiceItem: item,
            isItemChecked,
            isSelectedFinancingItemsEmpty,
        });

        return declarationAndInvoiceItem;
    });

    if (processedDeclarationAndInvoiceItems.some((item) => item.patientInvoice.isCounterBooked)) {
        return addPatientInvoiceCounterBookedItems(declarationAndInvoiceItems, exportStatus);
    }

    return processedDeclarationAndInvoiceItems;
}

function addPatientInvoiceCounterBookedItems(items: Financing[], exportStatus?: ExportStatusFilter) {
    const patientInvoiceCounterBookedItems = items.filter((item) => item.patientInvoice.isCounterBooked);

    for (const item of patientInvoiceCounterBookedItems) {
        const newRow = {
            ...item,
            patientInvoice: {
                ...item.patientInvoice,
                counterBooking: item.patientInvoice.id,
                id: uuidv4(),
                isCounterBooked: false,
                runNumber: item.patientInvoice.counterBookingRunNumber,
            },
        };

        const index = items.map((i) => i.patientInvoice.id).indexOf(item.patientInvoice.id);

        if (exportStatus === ExportStatusFilter.notExported && item.patientInvoice.runNumber) {
            items.splice(index, 1, newRow);
        } else if (
            (exportStatus === ExportStatusFilter.exported && item.patientInvoice.counterBookingRunNumber) ||
            (exportStatus === ExportStatusFilter.notExported && !item.patientInvoice.counterBookingRunNumber) ||
            exportStatus === ExportStatusFilter.both
        ) {
            items.splice(index + 1, 0, newRow);
        }
    }

    return items;
}

function processDeclarationAndInvoiceItem(args: ProcessDeclarationAndInvoiceItemArgs) {
    const { declarationAndInvoiceItem, isItemChecked, isSelectedFinancingItemsEmpty, allFinanceItemsSelected } = args;

    declarationAndInvoiceItem.isMigratedProcess = isMigratedProcess(declarationAndInvoiceItem);
    declarationAndInvoiceItem.checked = isSelectedFinancingItemsEmpty
        ? false
        : (allFinanceItemsSelected && !declarationAndInvoiceItem.isMigratedProcess) || isItemChecked;

    return declarationAndInvoiceItem;
}

function isMigratedProcess(activity: Financing): boolean {
    return (
        activity.notFundedReason === NotFundedReason.migration ||
        (isNonNullable(activity.migrationDateTime) &&
            isNonNullable(activity.diagnosticReportDateSent) &&
            activity.migrationDateTime >= activity.diagnosticReportDateSent)
    );
}

function isWrongSearchCondition(text: string | undefined, field: string | undefined) {
    if (text && field === 'processId') {
        const trimmedSearchText = normalizeSearchText(text);
        if (!searchTextMatchesProcessId(trimmedSearchText)) {
            return true;
        }
    }

    return false;
}
